import type { ReactNode } from "react";
import { FiArrowDown, FiArrowUp } from "react-icons/fi";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import { Loading } from "@/shared/ui/feedback/Loading";
import {
  type PullRefreshCallback,
  type PullRefreshPresentation,
  usePullRefreshPresenter,
} from "@/shared/ui/feedback/usePullRefreshPresenter";

/**
 * Kept for API compatibility with callers that used these as scroll offsets
 * during the scroll-based PTR era. This implementation drives animation with
 * CSS transform instead of real scrolling, so no spacer is needed.
 */
export const PULL_ZONE_HEIGHT = 0;
export const BOTTOM_SPACER_HEIGHT = 0;

export type IndicatorState = {
  progress: number;
  refreshing: boolean;
  opacity: number;
};

export type PullRefreshProps = {
  children: ReactNode;
  className?: string;
  onRefresh?: PullRefreshCallback;
  pullThreshold?: number;
  cooldownMs?: number;
  renderTopIndicator?: (state: IndicatorState) => ReactNode;
  renderBottomIndicator?: (state: IndicatorState) => ReactNode;
};

export type PullRefreshViewProps = {
  children: ReactNode;
  className?: string;
  containerRef: PullRefreshPresentation["containerRef"];
  topIndicator: IndicatorState | null;
  bottomIndicator: IndicatorState | null;
  renderTopIndicator: (state: IndicatorState) => ReactNode;
  renderBottomIndicator: (state: IndicatorState) => ReactNode;
};

function DefaultTopIndicator({
  progress,
  refreshing,
  opacity,
}: IndicatorState): ReactNode {
  const rotation = Math.min(progress, 100) * 1.8;
  return (
    <div
      className="fixed left-0 right-0 flex justify-center pointer-events-none z-30"
      style={{ top: "calc(env(safe-area-inset-top) + 74px)", opacity }}
    >
      {refreshing ? (
        <Loading size="sm" />
      ) : (
        <FiArrowDown
          className="text-foreground"
          size={20}
          style={{ transform: `rotate(${rotation}deg)` }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}

function DefaultBottomIndicator({
  progress,
  refreshing,
  opacity,
}: IndicatorState): ReactNode {
  const rotation = Math.min(progress, 100) * 1.8;
  return (
    <div
      className="fixed left-0 right-0 flex justify-center pointer-events-none z-30"
      style={{ bottom: "130px", opacity }}
    >
      {refreshing ? (
        <Loading size="sm" />
      ) : (
        <FiArrowUp
          className="text-foreground"
          size={20}
          style={{ transform: `rotate(${-rotation}deg)` }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}

export function PullRefresh({
  children,
  className,
  onRefresh,
  pullThreshold = 120,
  cooldownMs = 800,
  renderTopIndicator = DefaultTopIndicator,
  renderBottomIndicator = DefaultBottomIndicator,
}: PullRefreshProps): ReactNode {
  const presentation = usePullRefreshPresenter({
    onRefresh,
    pullThreshold,
    cooldownMs,
  });
  const { node, ...viewProps } = presentation;

  return (
    <ComponentScope node={node}>
      <PullRefreshView
        {...viewProps}
        className={className}
        renderTopIndicator={renderTopIndicator}
        renderBottomIndicator={renderBottomIndicator}
      >
        {children}
      </PullRefreshView>
    </ComponentScope>
  );
}

export function PullRefreshView({
  children,
  className,
  containerRef,
  topIndicator,
  bottomIndicator,
  renderTopIndicator,
  renderBottomIndicator,
}: PullRefreshViewProps): ReactNode {
  return (
    <>
      <div
        ref={containerRef}
        className={className}
        style={{ willChange: "transform" }}
      >
        {children}
      </div>
      {topIndicator && renderTopIndicator(topIndicator)}
      {bottomIndicator && renderBottomIndicator(bottomIndicator)}
    </>
  );
}
